import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CustomDialog, CustomTextField } from "../../../../../../common/components";
import { translate } from "../../../../../../common/providers";
import styles from "./ThemeFormDialog.module.css";

export default function ThemeFormDialog({ onClose, onSubmit, open, theme = null }) {
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [nameError, setNameError] = useState(false);

	const isEditMode = !!theme;

	useEffect(() => {
		if (open) {
			if (theme) {
				setName(theme.name || "");
				setDescription(theme.description || "");
			} else {
				setName("");
				setDescription("");
			}
			setNameError(false);
		}
	}, [open, theme]);

	const handleSubmit = useCallback(() => {
		if (!name.trim()) {
			setNameError(true);
			return;
		}
		onSubmit({
			name: name.trim(),
			description: description.trim(),
		});
	}, [name, description, onSubmit]);

	const handleNameChange = (e) => {
		const newValue = e.target.value;
		setName(newValue);
		setNameError(!newValue.trim());
	};

	const handleDescriptionChange = (e) => {
		setDescription(e.target.value);
	};

	const handleReset = () => {
		setName("");
		setDescription("");
		setNameError(false);
	};

	return (
		<CustomDialog
			displayCloseCross
			isForm
			closeLabel={translate("common:btn.cancel")}
			maxWidth="sm"
			open={open}
			submitDisabled={!name.trim()}
			submitLabel={translate("common:btn.validate")}
			title={
				isEditMode
					? translate("dsi.commitment-definition.theme.edit")
					: translate("dsi.commitment-definition.theme.new")
			}
			onClose={onClose}
			onExited={handleReset}
			onSubmit={handleSubmit}
		>
			<div className={styles.formContainer}>
				<div className={styles.fieldGroup}>
					<label className={styles.label} htmlFor="theme-name">
						{translate("dsi.commitment-definition.theme.name")}
					</label>
					<CustomTextField
						autoFocus
						fullWidth
						required
						error={nameError}
						helperText={nameError && translate("common:error.required")}
						id="theme-name"
						placeholder={translate("dsi.commitment-definition.theme.name")}
						size="small"
						value={name}
						variant="outlined"
						onChange={handleNameChange}
					/>
				</div>
				<div className={styles.fieldGroup}>
					<label className={styles.label} htmlFor="theme-description">
						{translate("dsi.commitment-definition.theme.description")}
					</label>
					<CustomTextField
						fullWidth
						multiline
						id="theme-description"
						minRows={8}
						placeholder={translate("dsi.commitment-definition.theme.description")}
						value={description}
						variant="outlined"
						onChange={handleDescriptionChange}
					/>
				</div>
			</div>
		</CustomDialog>
	);
}

ThemeFormDialog.propTypes = {
	onClose: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	open: PropTypes.bool.isRequired,
	theme: PropTypes.shape({
		id: PropTypes.number,
		name: PropTypes.string,
		description: PropTypes.string,
	}),
};

ThemeFormDialog.defaultProps = {
	theme: null,
};
